import { SettingsIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Grid,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { usePremium } from "../../../contexts/Premium";
import {
  getUserInfo,
  getUserNotes,
  getUserStats,
} from "../../../services/userApi";
import { getGoals } from "../../../services/goalsApi";
import ProfileNotesList from "../components/ProfileNotesList";
import ReadingStreakCard from "../components/ReadingStreakCard";
import GoalsTab from "../components/goals/GoalsTab";
import GoalsUpgrade from "../components/goals/GoalsUpgrade";
import { findLiveRateGoal } from "../helpers/goals";

const TAB_INDEX = { stats: 0, notes: 1, goals: 2 };

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activityYear, setActivityYear] = useState(new Date().getFullYear());
  const [selectedTab, setSelectedTab] = useState(
    TAB_INDEX[location.state?.tab] ?? 0
  );
  // Entry points elsewhere in the app open the creation sheet pre-filled.
  const [goalCreateInitial, setGoalCreateInitial] = useState(
    location.state?.createGoal || null
  );

  useEffect(() => {
    if (location.state?.tab && TAB_INDEX[location.state.tab] !== undefined) {
      setSelectedTab(TAB_INDEX[location.state.tab]);
    }
    if (location.state?.createGoal) {
      setGoalCreateInitial(location.state.createGoal);
    }
  }, [location.state]);
  const { authData, updateUser, signOut } = useAuth();
  const { isPremium } = usePremium();
  const { data, isLoading } = useQuery(
    ["userStats", activityYear],
    () => getUserStats(undefined, activityYear),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const notesQuery = useQuery(["userNotes"], getUserNotes, {
    staleTime: 5 * 60 * 1000,
  });

  const goalsQuery = useQuery(["goals"], getGoals, {
    staleTime: 5 * 60 * 1000,
  });
  const rateGoal = findLiveRateGoal(goalsQuery.data?.data?.active || []);

  useQuery(["user"], getUserInfo, {
    onSuccess: (response) => {
      if (response.data) {
        updateUser(response.data);
      }
    },
  });

  const displayName =
    authData.userHandle || `${authData.firstName} ${authData.lastName}`;
  const daysRead = Object.keys(data?.readingActivity?.days || {}).length;

  const notes = Array.isArray(notesQuery.data?.data)
    ? notesQuery.data.data
    : [];

  const onPressProfileNote = (note) => {
    if (!note.comic?.id) {
      return;
    }

    navigate(`/comics/${note.comic.id}/view`, {
      state: {
        source: "profile",
        comic: {
          ...note.comic,
          notes: [{ ...note, isMine: true }],
          previewOnly: false,
          readingId: null,
        },
      },
    });
  };

  const onPressLastRead = (lastRead) => {
    if (!lastRead?.id) {
      return;
    }

    navigate(`/comics/${lastRead.id}/view`, {
      state: {
        source: "profile",
        comic: {
          ...lastRead,
          previewOnly: false,
          readingId: data?.lastReadOrder?.readingId || null,
        },
      },
    });
  };

  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
      <Box
        bg="gray.800"
        border="1px solid"
        borderColor="gray.700"
        borderRadius="xl"
        overflow="hidden"
      >
        <Flex
          align="center"
          justify="space-between"
          px={{ base: 5, md: 8 }}
          pt={{ base: 5, md: 7 }}
        >
          <Heading as="h1" fontSize={{ base: "2xl", md: "3xl" }}>
            Profile
          </Heading>
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="ghost"
            />
            <MenuList>
              <MenuItem onClick={() => navigate("/account")}>Profile</MenuItem>
              {!!isPremium && (
                <MenuItem onClick={() => navigate("/billing")}>
                  Billing
                </MenuItem>
              )}
              {!isPremium && (
                <MenuItem onClick={() => navigate("/go-premium")}>
                  Go Premium
                </MenuItem>
              )}
              <MenuItem onClick={() => navigate("/settings")}>Account</MenuItem>
              <MenuItem onClick={signOut}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        <Flex
          align="center"
          gap={{ base: 4, md: 5 }}
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 7 }}
        >
          <Avatar
            name={displayName}
            size={{ base: "lg", md: "xl" }}
            src={authData.profilePicture}
          />
          <Box flex="1" minW="0">
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }}>
              {displayName}
            </Heading>
            <Text color="gray.400" fontSize={{ base: "sm", md: "md" }}>
              {isPremium ? "Premium" : "Free"} plan
              {data?.memberSince ? ` · since ${data.memberSince}` : ""}
            </Text>
          </Box>
          {!isPremium && (
            <Button
              bg="#3a2200"
              color="#ffaa00"
              _hover={{ bg: "#4b2b00" }}
              borderRadius="full"
              onClick={() => navigate("/go-premium")}
              size="sm"
            >
              Upgrade
            </Button>
          )}
        </Flex>

        <Tabs
          index={selectedTab}
          onChange={setSelectedTab}
          variant="line"
          colorScheme="red"
          isLazy
        >
          <Box>
            <TabList px={{ base: 4, md: 6 }} borderBottom="none">
              <Tab
                fontWeight="700"
                letterSpacing="0.8px"
                textTransform="uppercase"
              >
                Stats
              </Tab>
              <Tab
                fontWeight="700"
                letterSpacing="0.8px"
                textTransform="uppercase"
              >
                Notes
              </Tab>
              <Tab
                fontWeight="700"
                letterSpacing="0.8px"
                textTransform="uppercase"
              >
                Goals
              </Tab>
            </TabList>
          </Box>
          <TabPanels>
            <TabPanel p="0">
              {isLoading ? (
                <Flex justify="center" py="12">
                  <Spinner />
                </Flex>
              ) : (
                <>
                  <Grid
                    borderTop="1px solid"
                    borderColor="gray.700"
                    templateColumns="repeat(3, 1fr)"
                  >
                    <ProfileStat
                      label="Comics read"
                      value={data?.comicsRead ?? 0}
                    />
                    <ProfileStat
                      borderLeft="1px solid"
                      borderColor="gray.700"
                      label="Orders completed"
                      value={data?.readingOrdersCompleted ?? 0}
                    />
                    <ProfileStat
                      borderLeft="1px solid"
                      borderColor="gray.700"
                      label="Days read"
                      value={daysRead}
                    />
                  </Grid>

                  <Divider borderColor="gray.700" />

                  <ReadingStreakCard
                    activity={data?.readingActivity}
                    longestStreak={data?.longestReadingStreak}
                    lastRead={data?.lastRead}
                    lastReadAt={data?.lastReadAt}
                    lastReadOrder={data?.lastReadOrder}
                    onLastReadPress={onPressLastRead}
                    onOpenGoals={() => setSelectedTab(TAB_INDEX.goals)}
                    onYearChange={setActivityYear}
                    rateGoal={rateGoal}
                    streak={data?.readingStreak}
                  />
                </>
              )}
            </TabPanel>
            <TabPanel p="0">
              <ProfileNotesList
                isError={notesQuery.isError}
                isLoading={notesQuery.isLoading}
                notes={notes}
                onComicPress={onPressProfileNote}
                onRetry={notesQuery.refetch}
              />
            </TabPanel>
            <TabPanel p="0">
              {isPremium ? (
                <GoalsTab
                  createInitial={goalCreateInitial}
                  onCreateHandled={() => setGoalCreateInitial(null)}
                />
              ) : (
                <GoalsUpgrade />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Container>
  );
};

const ProfileStat = ({ label, value, ...props }) => (
  <Box
    px={{ base: 2, md: 5 }}
    py={{ base: 5, md: 7 }}
    textAlign="center"
    {...props}
  >
    <Text
      fontSize={{ base: "2xl", md: "3xl" }}
      fontWeight="bold"
      lineHeight="1"
    >
      {value}
    </Text>
    <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }} mt="2">
      {label}
    </Text>
  </Box>
);

export default Profile;
